import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  House, Heart, Baby, Calendar, BookOpen, Camera, Envelope,
  Notebook, Syringe, Sparkle, ChatCircleDots, Crown, SignOut, List, X,
  Pulse, Pill, FileText, Plant, Hospital, Users, ChatsCircle, ListChecks,
  Person, UsersThree, Egg, MoonStars,
} from "@phosphor-icons/react";
import { SunBloom } from "@/components/vansha/Graphics";

const SECTIONS = [
  {
    label: "Daily",
    items: [
      { to: "/app/dashboard", label: "Today", icon: House, tid: "nav-dashboard" },
      { to: "/app/journey", label: "Feature tour", icon: Sparkle, tid: "nav-journey" },
      { to: "/app/assistant", label: "AI Companion", icon: ChatCircleDots, tid: "nav-assistant" },
      { to: "/app/journal", label: "Journal", icon: Notebook, tid: "nav-journal" },
    ],
  },
  {
    label: "Pregnancy",
    items: [
      { to: "/app/pregnancy", label: "Week-by-week", icon: Heart, tid: "nav-pregnancy" },
      { to: "/app/symptoms", label: "Symptoms", icon: Pulse, tid: "nav-symptoms" },
      { to: "/app/medications", label: "Medications", icon: Pill, tid: "nav-medications" },
      { to: "/app/nutrition", label: "Nutrition", icon: Plant, tid: "nav-nutrition" },
      { to: "/app/records", label: "Medical records", icon: FileText, tid: "nav-records" },
      { to: "/app/appointments", label: "Appointments", icon: Calendar, tid: "nav-appointments" },
      { to: "/app/delivery-prep", label: "Hospital readiness", icon: Hospital, tid: "nav-delivery" },
      { to: "/app/preconception", label: "Pre-conception", icon: Egg, tid: "nav-preconception" },
    ],
  },
  {
    label: "Baby",
    items: [
      { to: "/app/baby", label: "Baby twin", icon: Baby, tid: "nav-baby" },
      { to: "/app/newborn", label: "Newborn logs", icon: MoonStars, tid: "nav-newborn" },
      { to: "/app/vaccinations", label: "Vaccinations", icon: Syringe, tid: "nav-vaccinations" },
    ],
  },
  {
    label: "Family",
    items: [
      { to: "/app/family", label: "Family circle", icon: Users, tid: "nav-family" },
      { to: "/app/feed", label: "Family feed", icon: ChatsCircle, tid: "nav-feed" },
      { to: "/app/tasks", label: "Family tasks", icon: ListChecks, tid: "nav-tasks" },
      { to: "/app/father", label: "Father mode", icon: Person, tid: "nav-father" },
      { to: "/app/grandparent", label: "Grandparent mode", icon: UsersThree, tid: "nav-grandparent" },
      { to: "/app/community", label: "Community", icon: ChatsCircle, tid: "nav-community" },
    ],
  },
  {
    label: "Memory",
    items: [
      { to: "/app/timeline", label: "Timeline", icon: Sparkle, tid: "nav-timeline" },
      { to: "/app/memories", label: "Memories", icon: Camera, tid: "nav-memories" },
      { to: "/app/letters", label: "Letters", icon: Envelope, tid: "nav-letters" },
      { to: "/app/yearbook", label: "Yearbook", icon: BookOpen, tid: "nav-yearbook" },
    ],
  },
  {
    label: "Vansha",
    items: [
      { to: "/app/academy", label: "Academy", icon: BookOpen, tid: "nav-academy" },
      { to: "/app/membership", label: "Membership", icon: Crown, tid: "nav-membership" },
    ],
  },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const tierLabel = (user?.membership_tier || "free").toLowerCase();
  const tierBadgeClass =
    tierLabel === "legacy"
      ? "chip-vermilion"
      : tierLabel === "signature" || tierLabel === "care"
      ? "chip-marigold"
      : "chip-sage";

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      <aside
        className={`fixed md:sticky top-0 z-40 h-screen w-72 bg-vansha-surface border-r border-ink-10 transition-transform duration-300 flex flex-col ${
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center justify-between px-6 border-b border-ink-10 flex-shrink-0 relative overflow-hidden">
          <div className="absolute -right-4 -top-6 opacity-40 pointer-events-none">
            <SunBloom size={90} />
          </div>
          <Link
            to="/app/dashboard"
            className="font-serif text-2xl tracking-tight text-ink-900 flex items-baseline gap-1 relative z-10"
            data-testid="app-logo"
          >
            <span>Vansha</span>
            <span className="text-terracotta italic text-sm font-normal">·</span>
          </Link>
          <button
            className="md:hidden btn-ghost p-2 relative z-10"
            onClick={() => setOpen(false)}
            data-testid="sidebar-close"
          >
            <X weight="thin" size={20} />
          </button>
        </div>

        <nav className="px-3 py-4 flex-1 overflow-y-auto">
          {SECTIONS.map((section) => (
            <div key={section.label} className="mb-5">
              <p className="overline px-3 mb-2 text-ink-500">{section.label}</p>
              <div className="flex flex-col gap-0.5">
                {section.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={n.tid}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-colors ${
                        isActive
                          ? "bg-[#1B1533] text-white"
                          : "text-ink-700 hover:bg-vansha-blush hover:text-ink-900"
                      }`
                    }
                  >
                    <n.icon weight="thin" size={18} />
                    {n.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-ink-10 bg-vansha-surface flex-shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full bg-terracotta text-white flex items-center justify-center font-serif text-lg"
              data-testid="user-avatar"
            >
              {(user?.name || "V")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-ink-900 truncate" data-testid="user-name">
                {user?.name}
              </div>
              <div className="mt-0.5">
                <span className={`chip ${tierBadgeClass}`} data-testid="user-tier">
                  {tierLabel}
                </span>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="btn-ghost p-2"
              title="Sign out"
              data-testid="logout-btn"
            >
              <SignOut weight="thin" size={18} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden glass-header h-14 flex items-center justify-between px-4 sticky top-0 z-30">
          <button
            className="btn-ghost p-2"
            onClick={() => setOpen(true)}
            data-testid="sidebar-open"
          >
            <List weight="thin" size={22} />
          </button>
          <span className="font-serif text-xl">Vansha</span>
          <span className="w-9" />
        </header>
        <main className="flex-1 p-4 sm:p-8 lg:p-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
